'use client'
import { FC, useEffect, useRef } from "react";
import { Content } from "@prismicio/client";
import { SliceComponentProps, PrismicRichText } from "@prismicio/react";
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next'
import dynamic from "next/dynamic";

const Slider = dynamic(() => import("react-slick"), {
  ssr: false,
  loading: () => <div className="h-[660px] md:h-[530px] w-full" aria-hidden="true" />,
});

/**
 * Props for `BannerSlider`.
 */
export type BannerSliderProps = SliceComponentProps<Content.BannerSliderSlice>;

/**
 * Component for "BannerSlider" Slices.
 */
const BannerSlider: FC<BannerSliderProps> = ({ slice }) => {

  const hasMultipleSlides = slice.primary.images.length > 1;
  const sliderRef = useRef<HTMLDivElement>(null);

  const updateHiddenSlideFocus = () => {
    if (!sliderRef.current) return;

    sliderRef.current.querySelectorAll<HTMLElement>('.slick-slide[aria-hidden="true"]').forEach((slide) => {
      slide.setAttribute('inert', '');
      slide.querySelectorAll<HTMLElement>('a, button, input, select, textarea, [tabindex]').forEach((el) => {
        el.setAttribute('tabindex', '-1');
      });
    });

    sliderRef.current.querySelectorAll<HTMLElement>('.slick-slide:not([aria-hidden="true"])').forEach((slide) => {
      slide.removeAttribute('inert');
      slide.querySelectorAll<HTMLElement>('a, button, input, select, textarea, [tabindex]').forEach((el) => {
        if (el.getAttribute('tabindex') === '-1') {
          el.removeAttribute('tabindex');
        }
      });
    });
  };

  useEffect(() => {
    updateHiddenSlideFocus();
  });

  const settings = {
    autoplay: hasMultipleSlides,
    arrows: false,
    dots: hasMultipleSlides,
    infinite: hasMultipleSlides,
    pauseOnHover: false,
    speed: 1000,
    autoplaySpeed: 4000,
    slidesToShow: 1,
    slidesToScroll: 1,
    cssEase: "linear",
    swipe: true,
    touchMove: true,
    draggable: true,
    afterChange: (_index: number) => {
      updateHiddenSlideFocus();
    },
    responsive: [
      {
        breakpoint: 768,
        settings: {
          autoplay: false,
          infinite: false,
          speed: 150,
          swipe: true,
          touchMove: true,
          draggable: true
        },
      },
    ],
  };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation} 
      className={`w-full max-h-[620px] font-fellix ${hasMultipleSlides === true ? "mb-20 lg:mb-12" : ""}`}
    >
      <div ref={sliderRef}>
      <Slider {...settings}>
        {slice.primary.images.map((item: any, index: number)=>{
          const bg = item.background_color.toLowerCase();
          const textColor = bg === "green" || bg === "orange" ? "text-white" : "text-jade";
          const richTextColor = textColor === "text-white"
            ? "prose-p:!text-white prose-headings:!text-white prose-strong:!text-white prose-a:!text-white"
            : "prose-p:!text-jade prose-headings:!text-jade prose-strong:!text-jade prose-a:!text-jade";
          const ctaButtonClass = `${textColor === "text-white" ? "border-white" : "border-jade"} border bg-transparent rounded-full px-4 py-2`;
          const linkDestination = item.link?.url
            ? item.link.url.split("?")[0].split("#")[0].split("/").filter(Boolean).pop()?.replace(/-/g, " ")
            : "";
          const linkLabel = linkDestination
            ? `Learn more about ${linkDestination}`
            : (item.link_text || "Learn more");
          const genericCta = /^(learn more|read more)$/i.test((item.link_text || "").trim());
          const hiddenSuffix = genericCta && linkDestination ? ` about ${linkDestination}` : "";
          return(
              <div className={`${bg === "gray" ? "bg-roadmapdarkgrey" : bg}`} key={index}>
                    {item.background_full_width === true ? 
                      <div className="grid grid-cols-12 gap-0 w-full">
                        <div className="col-span-12 md:col-span-6">
                          <PrismicNextImage
                            field={item.image.FullWidth}
                            fallbackAlt=""
                            imgixParams={{ auto: ['compress'], fm: 'avif', q: 28, fit: 'max' }}
                            sizes="(min-width: 1280px) 412px, (min-width: 1024px) 30vw, (min-width: 768px) 50vw, 100vw"
                            priority={index === 0}
                            fetchPriority={index === 0 ? "high" : "auto"}
                          />
                          <div>
                            <article className={`text-[90px] leading-[90px] font-medium ${textColor} mt-9 ml-[57px] mb-8`}>
                              <PrismicRichText field={item.headline}/>
                            </article>
                            <div className={`${textColor} ${richTextColor}`}>
                            <PrismicRichText field={item.paragraph}/>
                            <PrismicNextLink
                              field={item.link}
                              className={`${textColor} ${ctaButtonClass} inline-flex items-center`}
                              aria-label={linkLabel}
                            >
                              {item.link_text}
                              {hiddenSuffix ? <span className="sr-only">{hiddenSuffix}</span> : null}
                            </PrismicNextLink>
                            </div>
                          </div>
                        </div>
                      </div>
                    :
                    <div className="grid grid-cols-12 gap-0 lg:h-screen lg:max-h-[530px] mx-auto">
                      <div className="col-span-12 md:col-span-6 relative h-[300px] md:h-auto lg:max-h-[640px] p-4 py-6 md:p-12 lg:p-16">
                        <article className={`text-[36px] leading-9 lg:text-[50px] lg:leading-[54px] xl:text-[90px] xl:leading-[90px] font-medium ${textColor} md:-ml-1`}>
                          <PrismicRichText field={item.headline}/>
                        </article>
                        <div className={`absolute left-4 md:left-12 lg:left-16 bottom-4 md:bottom-10 text-[17px] leading-5 lg:text-[28px] lg:leading-9 ${textColor} ${richTextColor} tracking-[.6px] max-w-[60%] lg:max-w-[480px] font-semibold mt-6 prose prose-h4:m-0 prose-h4:mb-4 prose-h4:font-normal prose-a:no-underline lg:pr-8`}>
                          <PrismicRichText field={item.paragraph}/>
                          {item.link?.link_type !=="Any" ? 
                          <PrismicNextLink
                            className={`font-normal text-[14px] leading-5 md:text-[16px] md:leading-6 inline-flex items-center mt-4 ${textColor} ${ctaButtonClass}`}
                            field={item.link}
                            aria-label={linkLabel}
                          >
                            {item.link_text}
                            {hiddenSuffix ? <span className="sr-only">{hiddenSuffix}</span> : null}
                          </PrismicNextLink>
                          :<></>}
                        </div>
                      </div>
                      <div className="col-span-12 md:col-span-6 h-[360px] md:h-auto lg:max-h-[640px]">
                          <PrismicNextImage
                            field={item.image}
                            fallbackAlt=""
                            className="w-full object-top object-cover max-h-[420px] lg:h-screen lg:max-h-[530px]"
                            imgixParams={{ auto: ['compress'], fm: 'avif', q: 28, fit: 'max' }}
                            sizes="(min-width: 1280px) 412px, (min-width: 1024px) 30vw, (min-width: 768px) 50vw, 100vw"
                            priority={index === 0}
                            fetchPriority={index === 0 ? "high" : "auto"}
                          />
                      </div>
                    </div>
                    }
              </div>
          )
        })}
      </Slider>
      </div>
    </section>
  );
};

export default BannerSlider;
